/**
 * 平台类型
 */
export enum Platform {
  APPLE = 'apple',
  ANDROID = 'android',
}

/**
 * 订单状态
 */
export enum OrderStatus {
  ACTIVE = 1, // 有效
  EXPIRED = 2, // 过期
  CANCELLED = 3, // 撤销/退款
}

/**
 * 订阅类型
 */
export enum SubscriptionType {
  MONTHLY = 'monthly', // 月付
  ANNUAL = 'annual', // 半年付（苹果 V1 历史 _6monthly SKU 用）
  YEARLY = 'yearly', // 年付（安卓 _yearly SKU 用）
  QUARTERLY = 'quarterly', // 季度付（苹果 pro_quarterly）
  TRIAL = 'trial', // 试用
}

/**
 * 会员等级：0=Pro，1=Premium，2=Android 显示 Basic / iOS 显示 Lite（根据 paymentChannel 判断）
 */
export enum MemberLevel {
  ADVANCED = 'advanced', // 0 Pro
  PREMIUM = 'premium', // 1 Premium
}

/**
 * 支付渠道
 */
export enum PaymentChannel {
  APP_STORE = 'app_store',
  GOOGLE_PAY = 'google_pay',
}

/**
 * 安卓订阅通知类型（Google Play RTDN）
 */
export enum AndroidSubscriptionNotificationType {
  SUBSCRIPTION_RECOVERED = 1, // 订阅从账户保留状态恢复
  SUBSCRIPTION_RENEWED = 2, // 订阅续订
  SUBSCRIPTION_CANCELED = 3, // 订阅被取消
  SUBSCRIPTION_PURCHASED = 4, // 新订阅购买
  SUBSCRIPTION_ON_HOLD = 5, // 订阅进入账户保留状态
  SUBSCRIPTION_IN_GRACE_PERIOD = 6, // 订阅进入宽限期
  SUBSCRIPTION_RESTARTED = 7, // 用户通过 Play > 账户 > 订阅恢复了订阅
  SUBSCRIPTION_PRICE_CHANGE_CONFIRMED = 8, // 用户成功确认订阅价格变化
  SUBSCRIPTION_DEFERRED = 9, // 订阅的续订时间点已延期
  SUBSCRIPTION_PAUSED = 10, // 订阅已暂停
  SUBSCRIPTION_PAUSE_SCHEDULE_CHANGED = 11, // 订阅暂停计划已更改
  SUBSCRIPTION_REVOKED = 12, // 用户在到期时间之前访问了订阅
  SUBSCRIPTION_EXPIRED = 13, // 订阅已到期
}

export const AndroidSubscriptionNotificationTypeLabelMap: Record<number, string> = {
  [AndroidSubscriptionNotificationType.SUBSCRIPTION_RECOVERED]: '订阅从账户保留状态恢复',
  [AndroidSubscriptionNotificationType.SUBSCRIPTION_RENEWED]: '订阅续订',
  [AndroidSubscriptionNotificationType.SUBSCRIPTION_CANCELED]: '订阅被取消',
  [AndroidSubscriptionNotificationType.SUBSCRIPTION_PURCHASED]: '新订阅购买',
  [AndroidSubscriptionNotificationType.SUBSCRIPTION_ON_HOLD]: '订阅进入账户保留状态',
  [AndroidSubscriptionNotificationType.SUBSCRIPTION_IN_GRACE_PERIOD]: '订阅进入宽限期',
  [AndroidSubscriptionNotificationType.SUBSCRIPTION_RESTARTED]: '用户通过 Play > 账户 > 订阅恢复了订阅',
  [AndroidSubscriptionNotificationType.SUBSCRIPTION_PRICE_CHANGE_CONFIRMED]: '用户成功确认订阅价格变化',
  [AndroidSubscriptionNotificationType.SUBSCRIPTION_DEFERRED]: '订阅的续订时间点已延期',
  [AndroidSubscriptionNotificationType.SUBSCRIPTION_PAUSED]: '订阅已暂停',
  [AndroidSubscriptionNotificationType.SUBSCRIPTION_PAUSE_SCHEDULE_CHANGED]: '订阅暂停计划已更改',
  [AndroidSubscriptionNotificationType.SUBSCRIPTION_REVOKED]: '用户在到期时间之前访问了订阅',
  [AndroidSubscriptionNotificationType.SUBSCRIPTION_EXPIRED]: '订阅已到期',
};

/**
 * 苹果订阅通知类型（App Store Server Notifications V2）
 * 后端 NotificationTypeV2.getValue() 返回字符串
 */
export const AppleSubscriptionNotificationTypeLabelMap: Record<string, string> = {
  SUBSCRIBED: '新订阅购买',
  DID_CHANGE_RENEWAL_PREF: '续订偏好变更（升降级）',
  DID_CHANGE_RENEWAL_STATUS: '续订状态变更（开关续订）',
  DID_FAIL_TO_RENEW: '自动续订失败',
  DID_RENEW: '订阅续费成功',
  EXPIRED: '订阅已过期',
  GRACE_PERIOD_EXPIRED: '宽限期已过',
  OFFER_REDEEMED: '优惠已兑换',
  PRICE_INCREASE: '订阅价格上涨',
  REFUND: '已退款',
  REFUND_DECLINED: '退款被拒',
  REFUND_REVERSED: '退款被撤回',
  RENEWAL_EXTENDED: '续订有效期已延长',
  RENEWAL_EXTENSION: '续订有效期延长中',
  REVOKE: '订阅被撤销',
  CONSUMPTION_REQUEST: '消耗品退款审批请求',
  TEST: '测试通知',
};

/**
 * 用户来源（设备类型名称）
 */
// export type SourceType = number;

/**
 * 搜索表单数据
 */
export interface SubscriptionSearchParams {
  platform?: Platform;
  suppId?: string;
  transactionId?: string;
  startDate?: string;
  endDate?: string;
  orderStatus?: OrderStatus;
  renewalStatus?: number; // 是否续订：1-是，0-否
  env?: number; // 订单环境：1-正式，0-测试（仅安卓平台）
  /** 通知类型筛选：安卓为数字枚举（1-13），苹果为字符串枚举（DID_RENEW 等） */
  notificationType?: number | string;
  currentPage?: number;
  pageSize?: number;
}

/**
 * 订阅记录
 */
export interface SubscriptionRecord {
  transactionId: string;
  subscriptionStartTime: string;
  subscriptionEndTime: string;
  currency: string;
  paymentAmount: number;
  suppId: string;
  username: string;
  sourceType: number;
  registerTime?: string;
  subscriptionType: SubscriptionType;
  /** 0=Plus，1=Premium，2=列表显示为 Basic(Lite) 由 paymentChannel 决定 */
  memberLevel: MemberLevel | number;
  /** 商品id（仅苹果订阅有） */
  productId?: string;
  /** 通知类型 */
  notificationType?: number | string;
  orderStatus: OrderStatus;
  remainingDays: number;
  renewalStatus: number; // 是否续订：1-是，0-否
  paymentChannel: PaymentChannel;
  afSource?: string; // appsflyer渠道来源
  afCampaign?: string; // appsflyer归因数据
  deviceType?: number; // 注册端(user_info.device_type)：1-iOS App，2-Android App，3-iOS H5，4-Android H5，5-PC Web
}

/**
 * 分页响应
 */
export interface SubscriptionListResponse {
  records: SubscriptionRecord[];
  total: number;
  currentPage: number;
  pageSize: number;
}
